'use client';

import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import Icon, { IconName } from '@/components/ui/Icon';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  useSidebar,
} from '@/components/ui/sidebar';
import MenuAvatar from './MenuAvatar';
import { privacyPolicyItems, settingsItems } from './MenuConfig';
import type { MenuItemType } from './MenuConfig';
import MenuItem from './MenuItem';
import styles from './MobileMenu.module.css';

export default function MobileMenu({
  menuItems,
  userId,
}: {
  menuItems: MenuItemType[];
  userId: string;
}) {
  const { setOpenMobile, openMobile } = useSidebar();
  const [isSettingMenuOpen, setIsSettingMenuOpen] = useState<boolean>(false);
  const t = useTranslations('menu');

  useEffect(() => {
    if (!openMobile) {
      // Reset menu position when sidebar is closed
      setTimeout(() => {
        setIsSettingMenuOpen(false);
      }, 300);
    }
  }, [openMobile]);

  return (
    <Sidebar side='right'>
      <SidebarHeader className='items-end pb-1'>
        <Button
          variant='ghost'
          size='icon'
          className='hover:bg-transparent md:hidden'
          onClick={() => setOpenMobile(false)}
        >
          <X className='h-6 w-6' />
        </Button>
      </SidebarHeader>
      <SidebarContent className='overflow-hidden'>
        <div
          className='flex flex-1 transition-transform duration-300 ease-in-out'
          style={{
            transform: isSettingMenuOpen
              ? 'translateX(-100%)'
              : 'translateX(0%)',
          }}
        >
          <div className='flex w-full shrink-0 flex-col px-6'>
            <SidebarGroup className='flex-1'>
              <MenuAvatar size={50} userId={userId} />

              <SidebarMenu className={styles.menuContainer}>
                <div className={`${styles.menuItems} overflow-y-auto`}>
                  {menuItems.map((item) => (
                    <MenuItem key={item.title} item={item} />
                  ))}
                </div>
              </SidebarMenu>
            </SidebarGroup>
            <div className='mt-auto py-6'>
              <SidebarMenu className='border-t pt-6'>
                <MenuItem
                  item={{
                    title: 'accountSettings',
                    icon: IconName.Settings,
                  }}
                  onClick={() => setIsSettingMenuOpen(true)}
                />
              </SidebarMenu>
            </div>
          </div>
          <div className='flex w-full shrink-0 flex-col items-start px-6'>
            <Button
              variant='ghost'
              className='mb-4 px-0 hover:bg-transparent'
              onClick={() => setIsSettingMenuOpen(false)}
            >
              <Icon variant={IconName.ArrowLeft} />
              <span className='sr-only'>{t('back')}</span>
            </Button>

            <SidebarGroup className='flex-1'>
              <SidebarMenu className={styles.menuItems}>
                {settingsItems.map((item) => (
                  <MenuItem key={item.title} item={item} color={item.color} />
                ))}
              </SidebarMenu>
            </SidebarGroup>
            <div className='mt-auto py-6'>
              <SidebarGroup>
                <SidebarMenu className='border-t pt-6'>
                  {privacyPolicyItems.map((item) => (
                    <MenuItem key={item.title} item={item} />
                  ))}
                </SidebarMenu>
              </SidebarGroup>
            </div>
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
}
